import { fmtDisplayDate } from '@utils/displayDate'
import { esc, fmtMoney } from './exagoAgedArGrid'

/**
 * Spec 209 §10: any Advanced (Exago) report's generic run (spec 209 §5) → the universal
 * grouped grid contract ({ columns, reportData, reportFooterRows, reportFooterCells }).
 *
 * cb-api has proved the printed structure against the report's own design and decided, in
 * integer cents, which columns may be re-added (`sum`). So every subtotal and total here is
 * EXAGO'S figure, placed as printed — nothing is summed on this side, exactly as spec 190's
 * `exagoAgedArGrid.js` places Kucker Marino's.
 *
 * Rules, each one deliberate:
 *
 *  - `columnId` is the column's `key` (an opaque string cb-api derives). The grouped grid
 *    keys widths, order, visibility, sorting and the frozen column (`pin:<columnId>`) on
 *    `String(columnId)`, so a string id persists like a number does.
 *  - money / number / percent → RIGHT + `filterTypeName: 'NUMBER'` (numeric: real numbers
 *    in every export). text → `dataType: 'TEXT'` and date → `dataType: 'DATE'`, CB's own
 *    vocabulary (reportGridUtils `declaredType`): a text column Exago right-aligns (a
 *    matter number "001129") is then never typed numeric by its alignment, never summed and
 *    never exported as a number.
 *  - percent → `percent: true, currency: false, decimals`: the export views write points
 *    with a literal "%" (spec 127 §4.6) and Show $ never touches it (`neverCurrency`).
 *  - money cells — records AND footers — are drawn from `value` with spec 190's `fmtMoney`
 *    and carry `dataValue`; Exago's money text is never placed. Number and percent cells
 *    keep Exago's text and carry `value` as `dataValue` (points for a percent) — except
 *    that a number printed in parentheses is drawn with a leading minus ("(3.50)" →
 *    "-3.50", Exago's own digits), so the shared grid can read the figure it shows.
 *  - number → `decimals` as cb-api counted them, so every export prints Exago's places.
 *  - `sum` → the aggregate 'SUM'; otherwise EXAGO_PRINTED_AGGREGATE (below).
 *  - Every Exago-originated string is escaped exactly ONCE with spec 190's `esc` before it
 *    becomes a `displayValue`, a `groupHeaderContent` or a footer cell — the grids inject
 *    those as HTML. A column `label` is NOT escaped: both grids, the column menus and every
 *    export writer render a label as TEXT, so escaping it here would print "&amp;" on
 *    screen and in every file.
 *  - Percent lines never enter the grid's footer arrays: under a money column the export
 *    writers would type "98.74%" as an amount. The page shows them in their own table.
 *
 * A plain module (no React) so the page file stays component-only.
 */

/**
 * The aggregate a column that may NOT be re-added declares. Not 'NONE' — that would let the
 * grid's sum-equals-total heuristic (`summableColumns`) run on Exago's figures, the float
 * guess spec 209 §8.8 exists to replace — and not AVG/MIN/MAX/COUNT, which the AI payload
 * names ("average") and the header tooltip describes ("averaged"). Checked against every
 * reader of the two aggregate fields: `summableColumns` (any non-SUM, non-NONE value is
 * excluded outright), `columnAggregate`/`columnAggregateScoped` + `AGGREGATE_PHRASE`
 * (no phrase for it), `nonSumReportAggregate` (publishes only AVG/MIN/MAX/COUNT) and
 * `AGGREGATE_WORDS` (none). So the column keeps Exago's figure, with the grid's "kept"
 * marker under a filter, and nothing ever labels it.
 */
export const EXAGO_PRINTED_AGGREGATE = 'EXAGO_PRINTED'

const FIGURE_KINDS = new Set(['money', 'number', 'percent'])
const ALIGNMENT = { LEFT: 'LEFT', RIGHT: 'RIGHT', CENTER: 'CENTER' }

const blank = (cell) => typeof cell?.text !== 'string' || cell.text.trim() === ''

const isoTime = (iso) => {
  const t = Date.parse(`${iso}T00:00:00`)
  return Number.isFinite(t) ? t : undefined
}

/** One grid column from one generic run column. */
export const gridColumn = (col) => {
  const figure = FIGURE_KINDS.has(col.kind)
  const aggregate = col.sum === true && (col.kind === 'money' || col.kind === 'number') ? 'SUM' : EXAGO_PRINTED_AGGREGATE
  return {
    columnId: col.key,
    label: col.header,
    visible: true,
    columnAlignment: figure ? 'RIGHT' : (ALIGNMENT[col.align] || 'LEFT'),
    ...(figure ? { filterTypeName: 'NUMBER' } : {}),
    ...(col.kind === 'text' ? { dataType: 'TEXT' } : {}),
    ...(col.kind === 'date' ? { dataType: 'DATE' } : {}),
    ...(col.kind === 'percent' ? { percent: true, currency: false, ...(Number.isInteger(col.decimals) ? { decimals: col.decimals } : {}) } : {}),
    // cb-api's precision for a plain number (the most places Exago printed in it). Without
    // it the export view reads the FIRST record's text, so "2" above "1.25" put 0 places on
    // the AI payload, the XLSX and the PDF (1 and 3 where Exago printed 1.25 and 3.25).
    ...(col.kind === 'number' && Number.isInteger(col.decimals) ? { decimals: col.decimals } : {}),
    reportFooterAggregateName: aggregate,
    groupFooterAggregateName: aggregate,
  }
}

/**
 * One grid cell. `footer` cells differ only for a date column: a footer may print a label
 * there ("Total"), which stays text (spec 209 §8.6).
 */
export const gridCell = (col, cell, { footer = false } = {}) => {
  const columnId = col.key
  if (blank(cell)) return { columnId, displayValue: '' }
  if (FIGURE_KINDS.has(col.kind)) {
    const value = cell.value
    // The shape guard (readGenericExagoRun) already refused this; a builder handed a
    // printed figure with no amount must not draw a blank where Exago printed a number.
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new Error('The report came back in a shape we could not read.')
    }
    if (col.kind === 'money') return { columnId, displayValue: fmtMoney(value), dataValue: value }
    // A negative plain number Exago prints in parentheses ("(3.50)") is drawn with a
    // leading minus and Exago's own digits ("-3.50"), in records and footers alike. The
    // shared grid reads a total's displayValue with `toNumber`, which takes "(3.50)" for
    // NaN: a filtered subtotal then kept Exago's figure WITHOUT the "kept" marker under a
    // bar saying it follows the filter, and a negative grand total dropped the column from
    // what may be re-added. Percent cells are untouched: cb-api's reader never accepts a
    // parenthesised percentage (`percentText`).
    const t = cell.text.trim()
    const m = col.kind === 'number' ? /^\((.*)\)$/.exec(t) : null
    return { columnId, displayValue: esc(m ? `-${m[1]}` : t), dataValue: value }
  }
  if (col.kind === 'date' && typeof cell.iso === 'string' && cell.iso) {
    const t = isoTime(cell.iso)
    return { columnId, displayValue: esc(fmtDisplayDate(cell.iso)), ...(t != null ? { dataValue: t } : {}) }
  }
  if (col.kind === 'date' && !footer) throw new Error('The report came back in a shape we could not read.')
  return { columnId, displayValue: esc(cell.text) }
}

const footerRows = (footer, cols) => (footer?.lines || []).map((line) => cols.map((col, i) => gridCell(col, line[i], { footer: true })))

const toRow = (node, cols) => {
  if (Array.isArray(node.cells)) {
    return { isGroup: false, dataCells: cols.map((col, i) => gridCell(col, node.cells[i])) }
  }
  const lines = footerRows(node.footer, cols)
  const label = typeof node.label === 'string' && node.label.trim() !== '' ? node.label : '(blank)'
  return {
    isGroup: true,
    groupHeaderContent: esc(label),
    dataRows: node.children.map((child) => toRow(child, cols)),
    // The whole printed block, Exago's order (total line first, then label lines)…
    ...(lines.length ? { groupFooterRows: lines } : {}),
    // …and line 0 as the subtotal the grid may re-add against, only when it IS a total.
    groupFooterCells: node.footer?.hasTotal ? lines[0] : [],
  }
}

/** The grouped grid's report object, or null before a run (or for an empty one). */
export const buildGenericGridReport = (data) => {
  if (!data || data.empty) return null
  const cols = data.columns
  const reportLines = footerRows(data.reportFooter, cols)
  return {
    columns: cols.map(gridColumn),
    reportData: data.tree.map((node) => toRow(node, cols)),
    ...(reportLines.length ? { reportFooterRows: reportLines } : {}),
    // Line 0 is the grand total the grid classifies SUM-safe columns against — only when
    // Exago printed one. A label-only footer gives the grid no total to re-add towards.
    reportFooterCells: data.reportFooter?.hasTotal ? reportLines[0] : [],
    recordCount: data.counts.records,
  }
}

/**
 * Exago's percent lines (spec 190's "% of total", generalised): every scope that printed
 * one, the whole report first, then each group by its label path. Text only, as printed —
 * for the page's own small table and the documents' criteria line, never the grid.
 * `[{ scope, cells: [{ index, header, text }] }]` (`scope` null = the whole report), blank
 * cells dropped; `index` is the column's position, since two text columns may share a header.
 */
export const percentLineRows = (data) => {
  if (!data) return []
  const cols = data.columns
  const out = []
  const add = (scope, footer) => {
    for (const line of footer?.percentLines || []) {
      const cells = line
        .map((c, i) => ({ index: i, header: cols[i].header, text: typeof c?.text === 'string' ? c.text.trim() : '' }))
        .filter((c) => c.text !== '')
      if (cells.length) out.push({ scope, cells })
    }
  }
  add(null, data.reportFooter)
  const walk = (nodes, path) => {
    for (const n of nodes || []) {
      if (!Array.isArray(n.children)) continue
      const label = typeof n.label === 'string' && n.label.trim() !== '' ? n.label : '(blank)'
      const here = [...path, label]
      add(here.join(' › '), n.footer)
      walk(n.children, here)
    }
  }
  walk(data.tree, [])
  return out
}
